import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { api } from '@/api/repository/axios';
import SeminarCard, { SeminarCardData } from '@/components/domain/seminar/SeminarCard';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import { PRIMARY } from './detailStyles';

/*
 * 세미나 상세를 보여 줄 수 없을 때(2026-09-29 사용자 "제대로 된 문구로").
 * - notFound: 서버 404 — 삭제·비공개, 또는 아직 공개되지 않은 명치사 커리어 세미나(목록과 같은 기준으로 숨김)
 * - failed: 네트워크·서버 오류 — 다시 시도
 * 신청했던 사람이 알림·메일에서 들어올 수 있어 로그인 상태면 [신청 내역 보기]를, 비로그인이면 [로그인]을 함께 둔다.
 * 막다른 화면이 되지 않게 아래에 "이런 세미나는 어때요?" — 목록 첫 페이지(커리어 우선) 3개(모바일 2개).
 */

const RECOMMEND_COUNT: number = 3;

const Recommend = styled.section`
  max-width: 1080px;
  margin: 0 auto 64px;
  padding: 0 16px;
`;

const RecommendHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 800;
    color: #111827;
  }

  button {
    border: none;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: ${PRIMARY};
    cursor: pointer;
  }
`;

const RecommendGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;

    & > *:nth-of-type(n + 3) {
      display: none;
    }
  }
`;

const Wrap = styled.section`
  max-width: 420px;
  margin: 72px auto;
  padding: 0 16px;
  text-align: center;

  @media (max-width: 768px) {
    margin: 48px auto;
  }
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: #eef4f3;
  color: ${PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 32px;
  }
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.4;
  color: #111827;
`;

const Body = styled.p`
  margin: 0 0 28px;
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  white-space: pre-line;
  word-break: keep-all;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const baseButton = `
  height: 48px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
`;

const PrimaryAction = styled.button`
  ${baseButton}
  border: none;
  background: ${PRIMARY};
  color: #fff;

  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

const SecondaryAction = styled.button`
  ${baseButton}
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;

  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

interface DetailUnavailableProps {
  reason: 'notFound' | 'failed';
  isLoggedIn: boolean;
  onBrowse: () => void;
  onRetry: () => void;
  onHistory: () => void;
  onLogin: () => void;
  onOpenSeminar: (id: string | number) => void;
  /** 지금 열려던 세미나 — 추천에서 뺀다 */
  currentId?: string;
}

/** 추천 세미나 — 실패하면 조용히 비운다 */
const RecommendedSeminars: React.FC<{ currentId?: string; onOpen: (id: string | number) => void; onBrowse: () => void }> = ({ currentId, onOpen, onBrowse }) => {
  const [items, setItems] = useState<SeminarCardData[]>([]);
  useEffect(() => {
    let alive: boolean = true;
    api.seminar.getList(RECOMMEND_COUNT + 1, 0, { sort: 'careerFirst' })
      .then((res) => {
        if (!alive || res?.code !== 0) return;
        const list: SeminarCardData[] = (res.result?.list ?? []) as SeminarCardData[];
        setItems(list.filter((item) => String(item.id) !== currentId).slice(0, RECOMMEND_COUNT));
      })
      .catch(() => { /* 추천 없이 둔다 */ });
    return () => { alive = false; };
  }, [currentId]);
  if (items.length === 0) return null;
  return (
    <Recommend aria-labelledby="seminar-recommend-title">
      <RecommendHead>
        <h2 id="seminar-recommend-title">이런 세미나는 어때요?</h2>
        <button type="button" onClick={onBrowse}>전체 보기</button>
      </RecommendHead>
      <RecommendGrid>
        {items.map((item) => (
          <SeminarCard key={item.id} seminar={item} onClick={onOpen} />
        ))}
      </RecommendGrid>
    </Recommend>
  );
};

const DetailUnavailable: React.FC<DetailUnavailableProps> = ({ reason, isLoggedIn, onBrowse, onRetry, onHistory, onLogin, onOpenSeminar, currentId }) => {
  if (reason === 'failed') {
    return (
      <Wrap aria-labelledby="seminar-unavailable-title">
        <IconCircle aria-hidden="true"><WifiOffOutlinedIcon /></IconCircle>
        <Title id="seminar-unavailable-title">세미나를 불러오지 못했어요</Title>
        <Body>{'인터넷 연결을 확인한 뒤\n다시 시도해 주세요.'}</Body>
        <Actions>
          <PrimaryAction type="button" onClick={onRetry}>다시 시도</PrimaryAction>
          <SecondaryAction type="button" onClick={onBrowse}>세미나 둘러보기</SecondaryAction>
        </Actions>
      </Wrap>
    );
  }
  return (
    <>
    <Wrap aria-labelledby="seminar-unavailable-title">
      <IconCircle aria-hidden="true"><EventBusyOutlinedIcon /></IconCircle>
      <Title id="seminar-unavailable-title">지금은 볼 수 없는 세미나예요</Title>
      <Body>
        {isLoggedIn
          ? '삭제됐거나 공개가 끝난 세미나예요.\n신청한 세미나라면 신청 내역에서 확인할 수 있어요.'
          : '삭제됐거나 공개가 끝난 세미나예요.\n로그인하면 볼 수 있는 세미나일 수도 있어요.'}
      </Body>
      <Actions>
        <PrimaryAction type="button" onClick={onBrowse}>다른 세미나 둘러보기</PrimaryAction>
        {isLoggedIn ? (
          <SecondaryAction type="button" onClick={onHistory}>신청 내역 보기</SecondaryAction>
        ) : (
          <SecondaryAction type="button" onClick={onLogin}>로그인</SecondaryAction>
        )}
      </Actions>
    </Wrap>
    <RecommendedSeminars currentId={currentId} onOpen={onOpenSeminar} onBrowse={onBrowse} />
    </>
  );
};

export default DetailUnavailable;
